import { create } from "zustand";
import { isAxiosError } from "axios";
import { Checkpoint, TableDetails } from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import * as historyServices from "@/components/canvas/detailsSide/tableDetails/TableHistory/HistoryServices";
import type { ReleaseHistoryEntry } from "@/components/canvas/detailsSide/tableDetails/TableHistory/HistoryServices";
import { showNotificationGlobal } from "stores/useNotificationStore";
const logger = createLogger("CheckpointStore");

interface CheckpointState {
  checkpoints: Record<string, Checkpoint[]>;
  loading: Record<string, boolean>;
  /** Why a table's list last failed to load; gone once a load succeeds. */
  loadFailed: Record<string, string>;
  /** Checkpoint id of each table's released version (null = no release). */
  releasedCheckpointId: Record<string, string | null>;
  /** Version number of each table's released version (null = no release). */
  releasedVersion: Record<string, number | null>;
  /** Release-history timeline per table (newest first). */
  releaseHistory: Record<string, ReleaseHistoryEntry[]>;
  hasCheckpointAtLatestVersion: (
    tableId: string,
    tableDetails: TableDetails | null,
  ) => boolean;
}

interface CheckpointActions {
  loadCheckpoints: (
    tableId: string,
    tableDetails: TableDetails,
  ) => Promise<void>;
  createCheckpoint: (
    tableId: string,
    alias: string,
    tableVersion: number,
    createdBy: string,
  ) => Promise<Checkpoint>;
  loadRelease: (tableId: string) => Promise<void>;
  loadReleaseHistory: (tableId: string) => Promise<void>;
  setRelease: (tableId: string, version: number) => Promise<void>;
  clearRelease: (tableId: string) => Promise<void>;
  reset: () => void;
}

type CheckpointStore = CheckpointState & CheckpointActions;

const initialState: CheckpointState = {
  checkpoints: {},
  loading: {},
  loadFailed: {},
  releasedCheckpointId: {},
  releasedVersion: {},
  releaseHistory: {},

  hasCheckpointAtLatestVersion: () => false,
};

const releaseOf = (
  state: CheckpointState,
  tableId: string,
  checkpointId: string | null,
  version: number | null,
) => ({
  releasedCheckpointId: {
    ...state.releasedCheckpointId,
    [tableId]: checkpointId,
  },
  releasedVersion: { ...state.releasedVersion, [tableId]: version },
});

export const useCheckpointStore = create<CheckpointStore>((set, get) => ({
  ...initialState,
  hasCheckpointAtLatestVersion: (
    tableId: string,
    tableDetails: TableDetails | null,
  ) => {
    if (!tableDetails) return false;
    const currentTableVersion = Number(tableDetails.version);
    return (get().checkpoints[tableId] ?? []).some(
      (checkpoint) =>
        Number(checkpoint.table_version_number()) === currentTableVersion,
    );
  },

  loadCheckpoints: async (tableId: string, tableDetails: TableDetails) => {
    try {
      set((state) => ({
        loading: { ...state.loading, [tableId]: true },
      }));

      const loadedCheckpoints = await historyServices.loadCheckpoints(
        tableId,
        tableDetails,
      );

      set((state) => {
        const loadFailed = { ...state.loadFailed };
        delete loadFailed[tableId];
        return {
          checkpoints: { ...state.checkpoints, [tableId]: loadedCheckpoints },
          loading: { ...state.loading, [tableId]: false },
          loadFailed,
        };
      });

      logger.info(
        "Successfully loaded %d checkpoints for table %s",
        loadedCheckpoints.length,
        tableId,
      );
    } catch (err) {
      logger.error("Failed to load checkpoints for table %s: %o", tableId, err);
      // The list stays as it was: an empty one would read as "no checkpoints yet".
      set((state) => ({
        loading: { ...state.loading, [tableId]: false },
        loadFailed: {
          ...state.loadFailed,
          [tableId]:
            isAxiosError(err) && !err.response
              ? "Connection lost"
              : "The server couldn't send it",
        },
      }));
      throw err;
    }
  },

  createCheckpoint: async (
    tableId: string,
    alias: string,
    tableVersion: number,
    createdBy: string,
  ) => {
    try {
      const newCheckpoint = await historyServices.createCheckpoint(
        tableId,
        alias,
        tableVersion,
        createdBy,
      );
      set((state) => ({
        checkpoints: {
          ...state.checkpoints,
          [tableId]: [newCheckpoint, ...(state.checkpoints[tableId] || [])],
        },
      }));
      logger.info("Successfully created checkpoint: %s", alias);
      return newCheckpoint;
    } catch (err) {
      logger.error("Failed to create checkpoint: %o", err);
      throw err;
    }
  },

  loadRelease: async (tableId: string) => {
    try {
      const release = await historyServices.getRelease(tableId);
      set((state) =>
        releaseOf(
          state,
          tableId,
          release?.checkpointId ?? null,
          release?.version ?? null,
        ),
      );
    } catch (err) {
      logger.error("Failed to load release for table %s: %o", tableId, err);
    }
  },

  loadReleaseHistory: async (tableId: string) => {
    try {
      const history = await historyServices.listReleases(tableId);
      set((state) => ({
        releaseHistory: { ...state.releaseHistory, [tableId]: history },
      }));
    } catch (err) {
      logger.error(
        "Failed to load release history for table %s: %o",
        tableId,
        err,
      );
    }
  },

  // Not optimistic: a refusal must not move the rocket.
  setRelease: async (tableId: string, version: number) => {
    try {
      const release = await historyServices.setRelease(tableId, version);
      set((state) =>
        releaseOf(state, tableId, release.checkpointId, release.version),
      );
      logger.info("Successfully set release for table %s", tableId);
    } catch (err) {
      logger.error("Failed to set release: %o", err);
      showNotificationGlobal(
        err instanceof Error
          ? err.message
          : "Couldn't release. Please try again.",
        "error",
      );
      throw err;
    }
    await get().loadReleaseHistory(tableId);
  },

  // Not optimistic: a refusal must not take the rocket down.
  clearRelease: async (tableId: string) => {
    try {
      await historyServices.clearRelease(tableId);
      set((state) => releaseOf(state, tableId, null, null));
      logger.info("Successfully cleared release for table %s", tableId);
    } catch (err) {
      logger.error("Failed to clear release: %o", err);
      showNotificationGlobal(
        err instanceof Error
          ? err.message
          : "Couldn't unrelease. Please try again.",
        "error",
      );
      throw err;
    }
  },

  reset: () => {
    set(initialState);
  },
}));
